import cn from 'classnames';
import { SocialNetworks } from '@ui/league/SocialNetworks';
import { SponsorsGrid } from '@ui/league/SponsorsGrid';
import type { SponsorsGridFormData } from '../../../league/components/SponsorsGrid/contracts';
import type { SocialNetworksFormData } from '../../../league/components/SocialNetworks/contracts';
import { Link } from 'react-router';
import { useNavigationsQuery } from '@data/navigations/useNavigationsQuery';
import { useTenantConfigQuery } from '@data/tenants';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { DeviceType, FooterComponentType } from '@repo/types';
import type { FooterComponentConfig, FooterComponents, NavigationNode } from '@repo/types';
import { useMemo, useState } from 'react';
import { Image } from '../../../common/components/Image';
import { arrowDownIcon } from '@ui/common/staticAssets.constants';
import NewsletterSubscribe from '../../../article/components/NewsletterSubscribe/NewsletterSubscribe';
import type { NewsletterSubscribeFormData } from '../../../article/components/NewsletterSubscribe/contracts';
import { useIsNewsletterEnabled } from '../../../article/components/NewsletterSubscribe/useIsNewsletterEnabled';
import Logo from '../Logo/Logo';
import FooterContactUs from './FooterContactUs';
import { resolveFooterComponentEntry } from './footerComponentDefaults';

function resolveFooterHref(url: string): string {
  if (url.startsWith('/') || /^https?:\/\//i.test(url)) return url;
  return url.includes('.') ? `https://${url}` : url;
}

interface FooterNavItemLabelProps {
  url: NavigationNode['url'];
  label: string;
}

function FooterNavItemLabel({ url, label }: FooterNavItemLabelProps) {
  if (!url) return <>{label}</>;
  return (
    <Link
      className="footer__link small-text"
      to={resolveFooterHref(url)}
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      {label}
    </Link>
  );
}

/**
 * Recursively filters nodes where visible === false, dropping the entire subtree
 * when a parent is hidden. Nodes without a visible field default to visible.
 */
function filterVisibleNodes(nodes: NavigationNode[]): NavigationNode[] {
  return nodes
    .filter((node) => node.visible !== false)
    .map((node) => ({
      ...node,
      children: node.children ? filterVisibleNodes(node.children) : node.children,
    }));
}

/**
 * Resolves the three configurable footer blocks (SC-1117) from a (possibly missing)
 * `footerComponents` map, and pre-casts each entry's loosely-typed `config` to its
 * component's own form-data shape. A present-but-invalid stored value (e.g.
 * `columns: -1`) is passed through as-is here — deliberately not "fixed" (see plan
 * edge cases); only a genuinely *missing* key falls back to a default.
 */
function useResolvedFooterComponentEntries(footerComponents: FooterComponents | undefined) {
  const sponsorsEntry = resolveFooterComponentEntry(footerComponents, FooterComponentType.SponsorsGrid);
  const newsletterEntry = resolveFooterComponentEntry(footerComponents, FooterComponentType.NewsletterSubscribe);
  const socialEntry = resolveFooterComponentEntry(footerComponents, FooterComponentType.SocialNetworks);

  return {
    sponsors: { entry: sponsorsEntry, config: sponsorsEntry.config as SponsorsGridFormData },
    newsletter: { entry: newsletterEntry, config: newsletterEntry.config as NewsletterSubscribeFormData },
    social: { entry: socialEntry, config: socialEntry.config as SocialNetworksFormData },
  };
}

type FooterSponsorsSectionProps = {
  entry: FooterComponentConfig;
  config: SponsorsGridFormData;
  sponsorColumns: number;
};

/** Block 3: Sponsors (SC-1117: toggle/configure via the Footer Components admin screen). */
function FooterSponsorsSection({ entry, config, sponsorColumns }: FooterSponsorsSectionProps) {
  if (!entry.enabled) {
    return null;
  }

  return (
    <>
      <div className="footer__sponsors">
        <SponsorsGrid {...config} columns={config.columns ?? sponsorColumns} />
      </div>
      <hr className="footer__divider" />
    </>
  );
}

type FooterNewsletterSectionProps = {
  entry: FooterComponentConfig;
  config: NewsletterSubscribeFormData;
};

/**
 * Block 4: Newsletter (SC-1117: toggle/configure via the Footer Components admin screen). The
 * tenant's newsletter kill switch outranks that toggle, and is checked here as well as inside the
 * form so the section's wrapper and divider go with it.
 */
function FooterNewsletterSection({ entry, config }: FooterNewsletterSectionProps) {
  const isNewsletterEnabled = useIsNewsletterEnabled();

  if (!entry.enabled || !isNewsletterEnabled) {
    return null;
  }

  return (
    <>
      <div className="footer__newsletter">
        <NewsletterSubscribe {...config} />
      </div>
      <hr className="footer__divider" />
    </>
  );
}

type FooterSocialSectionProps = {
  entry: FooterComponentConfig;
  config: SocialNetworksFormData;
};

/** Block 5: Social Media (SC-1117: toggle/configure via the Footer Components admin screen). */
function FooterSocialSection({ entry, config }: FooterSocialSectionProps) {
  if (!entry.enabled) {
    return null;
  }

  return (
    <>
      <div className="footer__social">
        <h3 className="footer__section-title">Follow Us On Social</h3>
        <SocialNetworks {...config} />
      </div>
      <hr className="footer__divider" />
    </>
  );
}

type FooterNavSectionsProps = {
  navigationNodes: NavigationNode[];
  isMobileOrTablet: boolean;
  selectedNode: string | null;
  onNodeSelect: (id: string) => void;
};

/**
 * Block 7: the per-tenant footer nav-link columns (unaffected by SC-1117).
 * Root items render as a `Link` whenever `node.url` is set (SC-1098) — on mobile/tablet
 * the label click stops propagation so it navigates instead of also toggling the accordion.
 */
function FooterNavSections({ navigationNodes, isMobileOrTablet, selectedNode, onNodeSelect }: FooterNavSectionsProps) {
  const handleToggleKeyDown = (event: React.KeyboardEvent, nodeId: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onNodeSelect(nodeId);
  };

  return (
    <div className="footer__nav-container">
      {navigationNodes.map((node) => {
        const isSelected = selectedNode === node.id;
        const listId = `footer-nav-list-${node.id}`;

        const hasChildren = !!node.children && node.children.length > 0;

        if (isMobileOrTablet && !hasChildren) {
          return (
            <nav key={node.id} className="footer__nav-section">
              <h4 className="footer__nav-section-header">
                <FooterNavItemLabel url={node.url} label={node.label} />
              </h4>
            </nav>
          );
        }

        return (
          <nav
            key={node.id}
            className={cn('footer__nav-section', {
              'footer__nav-section--selected': isSelected,
            })}
          >
            {isMobileOrTablet ? (
              <div
                className="footer__nav-section-header-wrapper"
                role="button"
                tabIndex={0}
                aria-expanded={isSelected}
                aria-controls={listId}
                onClick={() => onNodeSelect(node.id)}
                onKeyDown={(event) => handleToggleKeyDown(event, node.id)}
              >
                <h4 className="footer__nav-section-header">
                  <FooterNavItemLabel url={node.url} label={node.label} />
                </h4>
                <Image
                  className="footer__nav-section-dropdown-arrow"
                  src={arrowDownIcon}
                  width={24}
                  height={24}
                  alt=""
                />
              </div>
            ) : (
              <h4 className="footer__nav-section-header">
                <FooterNavItemLabel url={node.url} label={node.label} />
              </h4>
            )}
            <ul className="footer__list" id={listId}>
              {node.children?.map(({ url, label, id }) => (
                <li key={id}>
                  <FooterNavItemLabel url={url} label={label} />
                </li>
              ))}
            </ul>
          </nav>
        );
      })}
    </div>
  );
}

function Footer() {
  const deviceType = useDeviceType();
  // A config load failure (network error, 500, tenant-scoped auth issue) leaves `data`
  // undefined here — `useResolvedFooterComponentEntries` falls back to defaults per key
  // regardless, so the public footer never renders empty/broken (SC-1117 AC).
  const { data: navData } = useNavigationsQuery();
  const { data: tenantConfig } = useTenantConfigQuery();
  const isMobile = deviceType === DeviceType.mobile;
  const isTablet = deviceType === DeviceType.tablet;
  const isMobileOrTablet = isMobile || isTablet;

  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  const handleNodeSelection = (newSelectedNode: string) => {
    setSelectedNode(selectedNode === newSelectedNode ? null : newSelectedNode);
  };

  const tenantName = tenantConfig?.name || tenantConfig?.env?.entityName || '';
  // Fewer, wider tiles on phones so each sponsor logo reads clearly; tablet keeps
  // 4-up and desktop 6-up.
  let sponsorColumns = 6;
  if (isMobile) sponsorColumns = 3;
  else if (isTablet) sponsorColumns = 4;
  const rawNavigationNodes = isMobileOrTablet ? navData?.footer?.mobileNodes : navData?.footer?.desktopNodes;
  const navigationNodes = useMemo(() => {
    if (!rawNavigationNodes) return rawNavigationNodes;
    return filterVisibleNodes(rawNavigationNodes);
  }, [rawNavigationNodes]);
  const hasNavigation = navigationNodes && navigationNodes.length > 0;

  const { sponsors, newsletter, social } = useResolvedFooterComponentEntries(navData?.footerComponents);

  return (
    <footer className="footer">
      {/* 1. Logo */}
      <div className="footer__logo-container">
        <Logo />
      </div>

      {/* 2. Tenant name */}
      {tenantName && <h2 className="footer__title">{tenantName}</h2>}

      <hr className="footer__divider" />

      <FooterSponsorsSection entry={sponsors.entry} config={sponsors.config} sponsorColumns={sponsorColumns} />
      <FooterNewsletterSection entry={newsletter.entry} config={newsletter.config} />
      <FooterSocialSection entry={social.entry} config={social.config} />

      {/* 6. Contact Us */}
      <FooterContactUs isMobile={isMobileOrTablet} />

      <hr className="footer__divider" />

      {/* 7. Navigation */}
      {hasNavigation && (
        <FooterNavSections
          navigationNodes={navigationNodes}
          isMobileOrTablet={isMobileOrTablet}
          selectedNode={selectedNode}
          onNodeSelect={handleNodeSelection}
        />
      )}

      {/* 8. Legal links */}
      <div className="footer__legal">
        <Link className="footer__legal-link small-text" to="/privacy-policy">
          Privacy Policy
        </Link>
        <span className="footer__dot-separator text-m">&middot;</span>
        <Link className="footer__legal-link small-text" to="/contact-us">
          Contact Us
        </Link>
      </div>

      {/* 9. Copyright */}
      <div className="footer__copyright">
        <p className="footer__copyright-text small-text">
          &copy; {new Date().getFullYear()} {tenantName}
        </p>
        <p className="footer__copyright-rights small-text">All rights reserved.</p>
      </div>

      {/* 10. Powered by */}
      <div className="footer__powered-by">
        <p className="footer__powered-by-text small-text">Powered by Streamline Content</p>
      </div>
    </footer>
  );
}

export default Footer;
